import { useQuery } from 'react-query';
import { useMemo } from 'react';
import { StatisticsResponse, MatchEngineStatisticsResponse } from '../../@types/statistic';
import { getStatisticsByPosition, getMatchEngineStatistics, getSystemStatistics } from '../../api/statisticApi';
import { MATCH_CANDIDATES_STATUS } from '../../consts/enums';
import { FILTER_QUERY } from '../../pages/PositionDetailsPage/CandidateMatchesTable/query';
import { calculatePercentage, findStatisticItemCount } from './utils';

type useStatisticsType = {
  matches: {
    [key: string]: number,
  } | undefined,
  loadStatistics: boolean,
}

type useMatchEngineStatisticsType = {
  statistics: {
    total: number,
    relevanceRate: number,
    declineRate: number,
  },
  isError: boolean,
  isFetching: boolean,
  refetch: () => void,
}

export const useStatistics = (
  positionId?: string,
  accountId?: string,
): useStatisticsType => {
  const {
    data,
    isFetching,
  } = useQuery<StatisticsResponse>(
    [`statistics_${positionId}_${accountId}`],
    () => getStatisticsByPosition(positionId, accountId),
    { enabled: !!accountId && !!positionId },
  );

  const matches = useMemo(
    () => data?.matches.reduce((acc, matchStatistic) => {
      acc[matchStatistic.name] = matchStatistic.amount;

      return acc;
    }, {} as { [key: string]: number }),
    [data],
  );

  return {
    matches,
    loadStatistics: isFetching,
  };
};

export const useMatchEngineStatistics = (
  positionId: string,
  accountId: string,
  selectedIds: string[],
): useMatchEngineStatisticsType => {
  const {
    data,
    isError,
    refetch,
    isFetching,
  } = useQuery<MatchEngineStatisticsResponse>(
    [`match_engine_statistics_${accountId}_${positionId}`],
    () => {
      const query = {
        in: {
          [FILTER_QUERY.RUN_MATCH_FLOW_ID]: selectedIds,
        },
      };

      return getMatchEngineStatistics(accountId, positionId, query);
    },
  );

  const statsArray = data?.data || [];

  const total = findStatisticItemCount(statsArray, 'TOTAL')?.count ?? 0;

  const rejected = findStatisticItemCount(statsArray, MATCH_CANDIDATES_STATUS.REJECTED)?.count ?? 0;
  const promotionFinished = findStatisticItemCount(statsArray, MATCH_CANDIDATES_STATUS.PROMOTION_FINISHED)?.count ?? 0;

  const results =  {
    total,
    relevanceRate: calculatePercentage(promotionFinished, total),
    declineRate: calculatePercentage(rejected, total),
  };

  return {
    statistics: results,
    isFetching,
    isError,
    refetch,
  };
};

type useSystemStatisticsType = {
  data: SystemStatisticsResponse | undefined,
  isLoading: boolean,
}

export const useSystemStatistics = (
  daysBack: number,
): useSystemStatisticsType => {
  const {
    data,
    isLoading,
  } = useQuery<SystemStatisticsResponse>(
    [`system_statistics_${daysBack.toString()}`],
    () => {
      return getSystemStatistics(daysBack);
    },
  );

  return {
    isLoading,
    data,
  };
};
